import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import Card from '../../components/ui/Card';
import api from '../../services/api';

const COLORS = ['#00CEEE', '#10B981', '#EF4444', '#F59E0B', '#8B5CF6', '#EA580C', '#009995'];

export default function Analytics() {
  const [dashboard, setDashboard] = useState(null);
  const [taskTypes, setTaskTypes] = useState([]);
  const [empData, setEmpData] = useState([]);
  const [storeData, setStoreData] = useState([]);
  const [catData, setCatData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [dashRes, ttRes, empRes, storeRes, catRes] = await Promise.allSettled([
          api.get('/v1/analytics/dashboard'),
          api.get('/v1/analytics/task-types'),
          api.get('/v1/analytics/employees'),
          api.get('/v1/analytics/stores'),
          api.get('/v1/analytics/categories'),
        ]);
        if (dashRes.status === 'fulfilled' && dashRes.value?.data) setDashboard(dashRes.value.data);
        if (ttRes.status === 'fulfilled' && ttRes.value?.data) setTaskTypes(ttRes.value.data || []);
        if (empRes.status === 'fulfilled' && empRes.value?.data) setEmpData(empRes.value.data || []);
        if (storeRes.status === 'fulfilled' && storeRes.value?.data) setStoreData(storeRes.value.data || []);
        if (catRes.status === 'fulfilled' && catRes.value?.data) setCatData(catRes.value.data || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    }
    load();
  }, []);

  if (loading) return (
    <div style={{ textAlign: 'center', padding: 80 }}>
      <div style={{ width: 36, height: 36, borderRadius: '50%', border: '3px solid rgba(0,206,238,0.15)', borderTopColor: '#00CEEE', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
      <span style={{ color: '#94A3B8', fontSize: 13 }}>Загрузка аналитики...</span>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0F172A', letterSpacing: '-0.02em' }}>Аналитика</h1>

      {/* KPI summary */}
      {dashboard && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
          {[
            { label: 'Сотрудников', value: dashboard.total_employees || 0, color: '#00CEEE', bg: '#E6FAFB' },
            { label: 'Задач', value: dashboard.total_tasks || 0, color: '#10B981', bg: '#ECFDF5' },
            { label: 'ОСГ проверок', value: dashboard.osg_checks || 0, color: '#EF4444', bg: '#FEF2F2' },
            { label: 'Выкладок', value: dashboard.display_reports || 0, color: '#F59E0B', bg: '#FFFBEB' },
          ].map(k => (
            <div key={k.label} style={{
              background: '#fff', borderRadius: 14, padding: '20px',
              border: '1px solid #E2E8F0',
              transition: 'all 0.2s ease',
            }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.06)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: k.color, letterSpacing: '-0.02em' }}>{k.value}</div>
              <div style={{ fontSize: 13, color: '#94A3B8', marginTop: 2, fontWeight: 500 }}>{k.label}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 20 }}>
        <Card title="Типы задач">
          {taskTypes.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={taskTypes} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3} dataKey="value" nameKey="name">
                  {taskTypes.map((e, i) => <Cell key={i} fill={e.fill || COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 10, border: '1px solid #E2E8F0' }} />
                <Legend iconSize={10} wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: 'center', padding: 50, color: '#94A3B8' }}>Нет данных</div>
          )}
        </Card>

        <Card title="Рейтинг сотрудников">
          {empData.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={empData.slice(0, 5)} layout="vertical" margin={{ top: 0, right: 20, left: 80, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#94A3B8' }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: '#64748B' }} width={80} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 10, border: '1px solid #E2E8F0' }} />
                <Bar dataKey="tasks_completed" name="Задач" fill="#00CEEE" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: 'center', padding: 50, color: '#94A3B8' }}>Нет данных</div>
          )}
        </Card>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 20 }}>
        <Card title="Статистика по магазинам">
          {storeData.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={storeData} margin={{ top: 0, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#94A3B8' }} />
                <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 10, border: '1px solid #E2E8F0' }} />
                <Bar dataKey="tasks_completed" name="Задач" fill="#10B981" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: 'center', padding: 50, color: '#94A3B8' }}>Нет данных</div>
          )}
        </Card>

        <Card title="По категориям">
          {catData.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={catData} margin={{ top: 0, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="category_name" tick={{ fontSize: 10, fill: '#94A3B8' }} />
                <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 10, border: '1px solid #E2E8F0' }} />
                <Bar dataKey="total_value" name="Значение" fill="#F59E0B" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: 'center', padding: 50, color: '#94A3B8' }}>Нет данных</div>
          )}
        </Card>
      </div>
    </div>
  );
}
